export const highlightKeyPhrases = (
  content: string,
  keyPhrases: string[],
): string => {
  if (!content || !keyPhrases || keyPhrases.length === 0) return content

  // Create a DOM parser
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${content}</div>`, 'text/html')
  const container = doc.body.firstChild as HTMLElement

  // Sort phrases by length (descending) to prioritize longer matches
  const sortedPhrases = [...keyPhrases].sort((a, b) => b.length - a.length)

  // Get the text content without HTML tags
  const textContent = container.textContent || ''

  // Create a mapping between text positions and DOM nodes
  const positionMap = new Map<number, { node: Node; offset: number }>()
  let position = 0

  // Build the position map
  function mapTextPositions(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || ''
      for (let i = 0; i < text.length; i++) {
        positionMap.set(position + i, { node, offset: i })
      }
      position += text.length
    } else {
      for (const child of Array.from(node.childNodes)) {
        mapTextPositions(child)
      }
    }
  }

  mapTextPositions(container)

  // Find and highlight phrases (in reverse order to avoid position shifts)
  const highlights: { start: number; end: number }[] = []

  // Find all matches for all phrases
  for (const phrase of sortedPhrases) {
    let startPos = 0
    const lowerText = textContent.toLowerCase()
    const lowerPhrase = phrase.toLowerCase()

    while (startPos < textContent.length) {
      const index = lowerText.indexOf(lowerPhrase, startPos)
      if (index === -1) break

      const end = index + phrase.length

      // Check if this highlight overlaps with any existing highlights
      const overlaps = highlights.some(
        (h) =>
          (index >= h.start && index < h.end) ||
          (end > h.start && end <= h.end) ||
          (index <= h.start && end >= h.end),
      )

      if (!overlaps) {
        highlights.push({ start: index, end })
      }

      startPos = index + 1
    }
  }

  // Sort highlights in reverse order to apply them without affecting other positions
  highlights.sort((a, b) => b.start - a.start)

  // Apply highlights
  for (const { start, end } of highlights) {
    // Get the start and end positions in the DOM
    const startInfo = positionMap.get(start)
    const endInfo = positionMap.get(end - 1) // -1 because end is exclusive

    if (!startInfo || !endInfo) continue

    // Create a range that spans from start to end
    const range = document.createRange()
    range.setStart(startInfo.node, startInfo.offset)
    range.setEnd(endInfo.node, endInfo.offset + 1)

    // Extract the content and wrap it in a highlight span
    const fragment = range.extractContents()
    const span = document.createElement('span')
    span.className = 'highlight'
    span.appendChild(fragment)
    range.insertNode(span)
  }

  return container.innerHTML
}
